<template>
  <div v-if="isShowModuleModal" class="modal d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditMode ? 'Edit Resource' : 'Create Resource' }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeModuleModal"></button>
        </div>

        <!-- Form Content -->
        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <!-- Row 1: Parent Resource (if applicable), Module ID, Resource Code -->
            <div :class="formData.parentResourceId ? 'form-row form-row-3' : 'form-row'">
              <!-- Parent Resource (if child resource) -->
              <div class="form-group" v-if="formData.parentResourceId">
                <label class="form-label">
                  Parent Resource
                  <span class="badge-auto">🔒 locked</span>
                </label>
                <div class="module-id-display parent-path-display" style="white-space: normal; word-break: break-word;">
                  {{ parentOptions.find(o => o.id === formData.parentResourceId)?.label }}
                </div>
              </div>

              <!-- Module ID (auto-fetched, read-only) -->
              <div class="form-group" v-if="formData.moduleId">
                <label class="form-label">
                  Module ID
                  <span class="badge-auto">⚡auto-fetched</span>
                </label>
                <div class="module-id-display">{{ formData.moduleId }}</div>
              </div>

              <!-- Resource Code -->
              <div class="form-group">
                <label class="form-label required">Resource Code</label>
                <input
                  v-model="formData.resourceCode"
                  type="text"
                  placeholder="e.g. INV"
                  class="form-input"
                />
                <span v-if="errors.resourceCode" class="error-text">
                  {{ errors.resourceCode }}
                </span>
              </div>
            </div>

            <!-- Row 2: Resource Name, Permission Key, Resource Type -->
            <div class="form-row form-row-3">
              <!-- Resource Name -->
              <div class="form-group">
                <label class="form-label required">Resource Name</label>
                <input
                  v-model="formData.resourceName"
                  type="text"
                  placeholder="e.g. Inventory"
                  class="form-input"
                />
                <span v-if="errors.resourceName" class="error-text">
                  {{ errors.resourceName }}
                </span>
              </div>

              <!-- Permission Key -->
              <div class="form-group">
                <label class="form-label">Permission Key</label>
                <input
                  v-model="formData.permissionKey"
                  type="text"
                  placeholder="e.g. inventory.view"
                  class="form-input"
                />
              </div>

              <!-- Resource Type -->
              <div class="form-group">
                <label class="form-label">Resource Type</label>
                <input
                  v-model="formData.resourceType"
                  type="text"
                  placeholder="e.g. menu"
                  class="form-input"
                />
              </div>
            </div>

            <!-- Row 3: Display Order, Route Path -->
            <div class="form-row">
              <!-- Display Order -->
              <div class="form-group">
                <label class="form-label">Display Order</label>
                <input
                  v-model.number="formData.displayOrder"
                  type="number"
                  placeholder="0"
                  class="form-input"
                />
                <span v-if="errors.displayOrder" class="error-text">
                  {{ errors.displayOrder }}
                </span>
              </div>

              <!-- Route Path -->
              <div class="form-group">
                <label class="form-label">Route Path</label>
                <input
                  v-model="formData.routePath"
                  type="text"
                  placeholder="e.g. /inventory"
                  class="form-input"
                />
              </div>
            </div>

            <!-- Toggles -->
            <div class="form-group toggles-group">
              <label class="form-label">Settings</label>
              <div class="toggle-row">
                <label class="toggle-item">
                  <input type="checkbox" v-model="formData.isActive" />
                  <span>Active</span>
                </label>
                <label class="toggle-item">
                  <input type="checkbox" v-model="formData.isMenuVisible" />
                  <span>Menu Visible</span>
                </label>
                <label class="toggle-item">
                  <input type="checkbox" v-model="formData.isPermissionControlled" />
                  <span>Permission Controlled</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="modal-footer">
            <button
              type="button"
              class="button button-secondary"
              @click="closeModuleModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="button button-primary"
              :disabled="isSubmitting"
            >
              <span v-if="!isSubmitting">✓ {{ isEditMode ? 'Save Changes' : 'Create Resource' }}</span>
              <span v-else>{{ isEditMode ? 'Saving...' : 'Creating...' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script src="./appresourceModel.js"></script>

<style scoped>
* {
  box-sizing: border-box;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-row-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.form-label {
  display: flex;
  align-items: center;
  min-height: 18px;
  font-size: 10px;
  font-weight: 700;
  color: #014906;
  text-transform: none;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.form-label.required::after {
  content: ' *';
  color: #ef4444;
}

.form-input,
.form-select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  color: #1f2937;
  background-color: #ffffff;
  font-family: inherit;
  transition: all 0.2s;
  box-sizing: border-box;
  min-height: 31px;
}

.form-textarea {
  resize: vertical;
  min-height: 48px;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #14b8a6;
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
}

.form-input::placeholder {
  color: #9ca3af;
}

.error-text {
  display: block;
  font-size: 12px;
  color: #ef4444;
  margin-top: 6px;
}

.toggles-group .toggle-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
  font-weight: 400;
  text-transform: none;
}

.toggle-item input[type="checkbox"] {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: #2e4a2fb8;
}

.button {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.button-secondary {
  background-color: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.button-secondary:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.button-primary {
  background-color: #797a7b;
  color: white;
}

.button-primary:hover:not(:disabled) {
  background-color: #0d9488;
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
}

.button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.modal-title{
  font-size: 13px;
  font-weight: 700;
  color: #797a7b;
}
.modal-header{
  padding: 12px 18px;
}
.modal-footer{
  padding: .45rem;
}
.btn-close{
color: #6B7280;
}
.hint {
  font-weight: 400;
  color: #6b7280;
  font-size: 11px;
  margin-left: 4px;
}
.badge-auto {
  background: #d1fae5;
  color: #059669;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}
.module-id-display {
  background: #eef2ff;
  color: #4338ca;
  font-weight: 700;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 12px;
  min-height: 31px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

/* Responsive design */
@media (max-width: 768px) {
  .form-row-3 {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .form-row,
  .form-row-3 {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>